import type { Affix, AffixDef, AffixId, MasterDie } from './types';
import { AFFIX_BY_ID, AFFIX_POOL_WEIGHT, affixPoolFor } from '../data/affixes';
import { BALANCE } from '../data/balance';
import type { Rng } from './rng';

/** Arrondit une valeur tirée au pas de l'affixe (évite les 0,17000000000000004). */
function quantize(value: number, step: number): number {
  const decimals = Math.max(0, Math.ceil(-Math.log10(step) + 1e-9));
  return Number((Math.round(value / step) * step).toFixed(decimals));
}

/** Tire la valeur X d'un affixe dans sa plage. */
export function rollAffixValue(def: AffixDef, rng: Rng): number {
  if (def.min === def.max) return def.min;
  const raw = rng.float(def.min, def.max + def.step * 0.999);
  const v = quantize(raw, def.step);
  return Math.min(def.max, Math.max(def.min, v));
}

/** Affixe sans plage de valeur (Avantageux, Réplicant…) : Pipé n'en est pas (faces tirées). */
export function isFixedAffix(def: AffixDef): boolean {
  // A122-3, A122-4 : *Pipé* (`min === max`) compte comme un affixe à plage,
  // ses faces étant tirées à l'acquisition.
  return def.min === def.max && def.id !== 'loaded';
}

/**
 * L'affixe ne peut-il être porté **qu'une fois** par dé ? Les affixes
 * `unique` (*Pipé*, *Réplicant*, affixes nommés) et, depuis A122-3, tous les
 * affixes **sans plage** (`isFixedAffix` : *Avantageux*…).
 */
export function isOncePerDie(def: AffixDef): boolean {
  return def.unique === true || isFixedAffix(def);
}

/**
 * Tire `count` affixes pour un dé à `sides` faces.
 *
 * Le même affixe peut sortir plusieurs fois (spec §5.4) : le pool n'est pas
 * consommé — **sauf** pour les affixes marqués `unique` (*Pipé*, COMMENTS
 * v0.6.0 §1.3) et, depuis la v0.57.0 (A122-3), les affixes **sans plage**
 * (*Avantageux*…), retirés du pool dès qu'ils sont sortis une fois.
 *
 * `exclude` : affixes retirés du pool d'emblée (déjà portés par le dé — Forge).
 * Un pool vide arrête le tirage (moins d'affixes rendus, jamais de boucle).
 */
export function rollAffixes(
  sides: number,
  count: number,
  rng: Rng,
  exclude: Iterable<AffixId> = [],
): Affix[] {
  const pool = affixPoolFor(sides);
  const out: Affix[] = [];
  const taken = new Set<AffixId>(exclude);
  for (let i = 0; i < count; i++) {
    const choices = pool.filter((d) => !taken.has(d.id));
    const def = rng.weighted(choices, (d) => AFFIX_POOL_WEIGHT[d.pool]);
    if (!def) break;
    if (isOncePerDie(def)) taken.add(def.id);
    out.push({ id: def.id, value: rollAffixValue(def, rng) });
  }
  return out;
}

/** Nombre d'instances d'un affixe sur un dé. */
export function affixCount(die: MasterDie | null | undefined, id: AffixId): number {
  if (!die) return 0;
  let n = 0;
  for (const a of die.affixes) if (a.id === id) n += 1;
  return n;
}

/**
 * Facteur de **contexte** appliqué à l'affixe `id` : `factor` (le *Rat-Ailé*,
 * A123-4 : ×1,2) pour un affixe **à plage**, 1 pour un affixe sans plage
 * (*Avantageux*, *Réplicant*, affixes nommés — `isFixedAffix`).
 */
function contextFactor(id: AffixId, factor: number): number {
  if (factor === 1 || !(factor > 0)) return 1;
  const def = AFFIX_BY_ID[id];
  return def && !isFixedAffix(def) ? factor : 1;
}

/**
 * Valeur **effective** d'un affixe porté par `die` : sa valeur tirée, × 1,5
 * si le dé est **Enrichi** (v0.46.0, A94). C'est la valeur que le combat
 * applique et que l'interface affiche (`Game.affixValueOf`).
 *
 * `factor` (v0.58.0, A123-4) : facteur d'affixes du **contexte** — ×1,2 du
 * *Rat-Ailé* actif sur un dé équipé, 1 sinon — appliqué aux seuls affixes à
 * plage, multiplicatif avec Enrichi (×1,5 × 1,2).
 */
export function affixValueOf(
  die: Pick<MasterDie, 'enriched'> | null | undefined,
  affix: Affix,
  factor = 1,
): number {
  const base = die?.enriched === true ? affix.value * BALANCE.enrichedAffixFactor : affix.value;
  return base * contextFactor(affix.id, factor);
}

/**
 * Somme des valeurs d'un affixe porté par un dé (0 s'il ne le porte pas).
 * v0.46.0 (A94) : × 1,5 sur un dé **Enrichi** — *Lesté*, *Incisif*, *Précis*,
 * *Exécutant*, *Temporel*, *Doré*, *Empoisonné*, *Mutilant* ; *Avantageux*
 * reste un drapeau (valeur > 0), *Pipé* et les affixes nommés n'ont pas de
 * valeur utile.
 *
 * `factor` (v0.58.0, A123-4) : facteur d'affixes du contexte (*Rat-Ailé*),
 * 1 par défaut — voir `affixValueOf`.
 */
export function affixValue(die: MasterDie | null | undefined, id: AffixId, factor = 1): number {
  if (!die) return 0;
  let total = 0;
  for (const a of die.affixes) if (a.id === id) total += a.value;
  const enriched = die.enriched === true ? total * BALANCE.enrichedAffixFactor : total;
  return enriched * contextFactor(id, factor);
}

export function hasAffix(die: MasterDie | null | undefined, id: AffixId): boolean {
  if (!die) return false;
  return die.affixes.some((a) => a.id === id);
}

/**
 * Somme d'un affixe sur un ensemble de dés (Doré / Temporel d'un lancer) ;
 * `factor` : facteur d'affixes du contexte (*Rat-Ailé*, A123-4), 1 par défaut.
 */
export function sumAffix(dice: readonly (MasterDie | null)[], id: AffixId, factor = 1): number {
  let total = 0;
  for (const d of dice) total += affixValue(d, id, factor);
  return total;
}

/**
 * Affixe « dominant » d'un dé : celui dont la somme des valeurs est la plus
 * forte relativement à sa plage. Sert à nommer le dé.
 */
export function dominantAffix(die: MasterDie): AffixDef | null {
  const scores = new Map<AffixId, number>();
  for (const a of die.affixes) {
    const def = AFFIX_BY_ID[a.id];
    const span = def.max > 0 ? def.max : 1;
    scores.set(a.id, (scores.get(a.id) ?? 0) + a.value / span);
  }
  let best: AffixId | null = null;
  let bestScore = -1;
  for (const [id, score] of scores) {
    if (score > bestScore) {
      bestScore = score;
      best = id;
    }
  }
  return best ? AFFIX_BY_ID[best] : null;
}
